import { useTranslation } from 'react-i18next';
import {
  SectionFrame,
  Reveal,
  Stagger,
  StaggerItem,
  AnimatedNumber,
  Marquee,
} from '@/components/landing/kit';

/**
 * ProofSection — 職人 / The Living Ledger.
 *
 * A calm full-width trust strip below the hero. Three bands separated by
 * hairline rules: (1) a framing line with a single emerald accent word,
 * (2) the signature reach marquee (the section's only idle loop), and
 * (3) a 4-up proof-stat ledger row where each figure count-ups last.
 *
 * One emerald identity per viewport: the Band-1 underline word and the
 * Band-3 citation pill share the same emerald budget and are separated by
 * scroll distance. Zero warn-red here. Every reveal fires once and holds;
 * fully honours prefers-reduced-motion via the kit primitives.
 *
 * All copy authored via t(KEY, { defaultValue: EN }) under the fresh
 * lp.proof.* namespace — English renders with zero locale-JSON changes.
 */

const MARQUEE_ITEM_KEYS = [
  ['lp.proof.marquee_item_jp', 'JP  Tokyo · ¥ JPY'],
  ['lp.proof.marquee_item_us', 'US  SF Bay · $ USD'],
  ['lp.proof.marquee_item_de', 'DE  Berlin · € EUR'],
  ['lp.proof.marquee_item_gb', 'GB  London · £ GBP'],
  ['lp.proof.marquee_item_sg', 'SG  Singapore · S$ SGD'],
  ['lp.proof.marquee_item_kr', 'KR  Seoul · ₩ KRW'],
  ['lp.proof.marquee_item_cn', 'CN  Shanghai · ¥ CNY'],
  ['lp.proof.marquee_item_fr', 'FR  Paris · € EUR'],
  ['lp.proof.marquee_item_in', 'IN  Bengaluru · ₹ INR'],
  ['lp.proof.marquee_item_au', 'AU  Sydney · A$ AUD'],
  ['lp.proof.marquee_item_ca', 'CA  Toronto · C$ CAD'],
  ['lp.proof.marquee_item_br', 'BR  São Paulo · R$ BRL'],
] as const;

export function ProofSection() {
  const { t } = useTranslation();

  // Band 1: split the framing line on the §...§ sentinel so the one accent
  // word is wrapped in .underline-emerald. Graceful degradation: a locale
  // missing the sentinel renders plain (keeps the parity gate green).
  const framing = t('lp.proof.framing', {
    defaultValue:
      'Purpose-built for FP&A and 経営企画 teams who close across §borders§ — and have to defend every figure.',
  });
  const parts = framing.split('§');
  const hasAccent = parts.length === 3;

  const marqueeItems = MARQUEE_ITEM_KEYS.map(([key, fallback]) => {
    const raw = t(key, { defaultValue: fallback });
    // First whitespace-run separates the ISO/code segment from the label.
    const m = raw.match(/^(\S+)\s+(.*)$/);
    return {
      key,
      code: m ? m[1] : raw,
      label: m ? m[2] : '',
    };
  });

  return (
    <SectionFrame
      id="proof"
      ambient="none"
      rule
      className="bg-background py-20 md:py-28"
    >
      <div
        className="container mx-auto max-w-7xl px-4"
        aria-label={t('lp.proof.eyebrow', { defaultValue: 'Trusted reach' })}
      >
        {/* BAND 1 — FRAMING LINE */}
        <Stagger slow className="mx-auto max-w-2xl text-center">
          <StaggerItem variant="fadeIn">
            <span className="pill-slate font-mono text-[10.5px] uppercase tracking-[0.22em]">
              {t('lp.proof.eyebrow', { defaultValue: 'Trusted reach' })}
            </span>
          </StaggerItem>
          <StaggerItem variant="fadeInUp">
            <p className="display mt-5 text-balance text-xl leading-snug text-ink-800 md:text-2xl">
              {hasAccent ? (
                <>
                  {parts[0]}
                  <Reveal
                    as="span"
                    variant="drawLine"
                    delay={0.25}
                    className="underline-emerald origin-left inline"
                  >
                    {parts[1]}
                  </Reveal>
                  {parts[2]}
                </>
              ) : (
                framing
              )}
            </p>
          </StaggerItem>
        </Stagger>

        <div className="rule my-14 md:my-16" />

        {/* BAND 2 — REACH MARQUEE (signature idle loop) */}
        <Reveal variant="fadeIn">
          <div
            role="region"
            aria-label={t('lp.proof.marquee_label', {
              defaultValue: 'Consolidating today',
            })}
            className="flex items-center gap-6"
          >
            <span className="shrink-0 font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-400">
              {t('lp.proof.marquee_label', {
                defaultValue: 'Consolidating today',
              })}
            </span>

            <div className="relative min-w-0 flex-1 overflow-hidden">
              <Marquee durationSec={64} pauseOnHover={false} className="h-12">
                <div className="flex items-center gap-10 whitespace-nowrap pr-10">
                  {[...marqueeItems, ...marqueeItems].map((item, i) => (
                    <span
                      key={`${item.key}-${i}`}
                      className="inline-flex items-center gap-2 text-[13px] md:text-sm"
                    >
                      <span className="num text-ink-700">{item.code}</span>
                      {item.label && (
                        <>
                          <span aria-hidden="true" className="text-ink-300">
                            ·
                          </span>
                          <span className="text-ink-500">{item.label}</span>
                        </>
                      )}
                    </span>
                  ))}
                </div>
              </Marquee>

              {/* Edge fades — chips dissolve rather than clip. */}
              <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-background to-transparent md:w-24" />
              <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-background to-transparent md:w-24" />
            </div>
          </div>
        </Reveal>

        <div className="rule my-14 md:my-16" />

        {/* BAND 3 — PROOF STATS (4-up ledger row) */}
        <Stagger className="grid grid-cols-2 md:grid-cols-4 md:divide-x divide-border">
          {/* Stat 1 — countries / currencies reach */}
          <StaggerItem
            variant="fadeInUp"
            className="group px-4 py-6 transition-colors duration-200 hover:bg-ink-50/60 md:py-4"
          >
            <div className="figure-hero text-3xl text-ink-900 md:text-4xl">
              <AnimatedNumber
                value={12}
                suffix={t('lp.proof.stat1_value_suffix', { defaultValue: '+' })}
              />
            </div>
            <div className="mt-1 font-mono text-xs text-ink-400">
              {t('lp.proof.stat1_unit', { defaultValue: 'currencies' })}
            </div>
            <div className="display mt-3 text-sm text-ink-700">
              {t('lp.proof.stat1_label', {
                defaultValue: 'One consolidated P&L',
              })}
            </div>
            <p className="mt-2 text-xs leading-relaxed text-ink-500">
              {t('lp.proof.stat1_proof', {
                defaultValue:
                  'Per-account FX — P&L at average, BS at period-end, equity at historical. CTA balances itself.',
              })}
            </p>
          </StaggerItem>

          {/* Stat 2 — sub-second report latency */}
          <StaggerItem
            variant="fadeInUp"
            className="group px-4 py-6 transition-colors duration-200 hover:bg-ink-50/60 md:py-4"
          >
            <div className="figure-hero text-3xl text-ink-900 md:text-4xl">
              <AnimatedNumber
                value={1}
                prefix={t('lp.proof.stat2_value_prefix', { defaultValue: '<' })}
              />
            </div>
            <div className="mt-1 font-mono text-xs text-ink-400">
              {t('lp.proof.stat2_unit', { defaultValue: 'seconds' })}
            </div>
            <div className="display mt-3 text-sm text-ink-700">
              {t('lp.proof.stat2_label', {
                defaultValue: 'Board pack, on demand',
              })}
            </div>
            <p className="mt-2 text-xs leading-relaxed text-ink-500">
              {t('lp.proof.stat2_proof', {
                defaultValue:
                  'Materialized monthly aggregates — 14× faster at 250k facts, with a fail-safe live fallback.',
              })}
            </p>
          </StaggerItem>

          {/* Stat 3 — audit-grade cited insights (THE emerald cell) */}
          <StaggerItem
            variant="fadeInUp"
            className="group px-4 py-6 transition-colors duration-200 hover:bg-ink-50/60 md:py-4"
          >
            <div className="figure-hero text-3xl text-ink-900 md:text-4xl">
              <AnimatedNumber
                value={100}
                suffix={t('lp.proof.stat3_value_suffix', { defaultValue: '%' })}
              />
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span className="font-mono text-xs text-ink-400">
                {t('lp.proof.stat3_unit', { defaultValue: 'cited' })}
              </span>
              <span className="pill-emerald font-mono text-[10px] uppercase tracking-[0.12em]">
                {t('lp.proof.stat3_badge', { defaultValue: 'row-level cited' })}
              </span>
            </div>
            <div className="display mt-3 text-sm text-ink-700">
              {t('lp.proof.stat3_label', {
                defaultValue: 'Every AI insight, sourced',
              })}
            </div>
            <p className="mt-2 text-xs leading-relaxed text-ink-500">
              {t('lp.proof.stat3_proof', {
                defaultValue:
                  'Click any cell — the AI cites only the exact fact rows inside it. Guarded against invented sources.',
              })}
            </p>
          </StaggerItem>

          {/* Stat 4 — public pricing tiers */}
          <StaggerItem
            variant="fadeInUp"
            className="group px-4 py-6 transition-colors duration-200 hover:bg-ink-50/60 md:py-4"
          >
            <div className="figure-hero text-3xl text-ink-900 md:text-4xl">
              <AnimatedNumber value={3} />
            </div>
            <div className="mt-1 font-mono text-xs text-ink-400">
              {t('lp.proof.stat4_unit', { defaultValue: 'tiers, public' })}
            </div>
            <div className="display mt-3 text-sm text-ink-700">
              {t('lp.proof.stat4_label', {
                defaultValue: 'Pricing you can read',
              })}
            </div>
            <p className="mt-2 text-xs leading-relaxed text-ink-500">
              {t('lp.proof.stat4_proof', {
                defaultValue:
                  'Free, Growth, Enterprise — listed in the open. No sales call to learn the number.',
              })}
            </p>
          </StaggerItem>
        </Stagger>
      </div>
    </SectionFrame>
  );
}

export default ProofSection;
